import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiError } from '../../../core/auth/auth.models';
import { AuthService } from '../../../core/auth/auth.service';
import { OrganizationService } from '../../../core/organization/organization.service';
import { PageHeader } from '../../../shared/page-header/page-header';

@Component({
  selector: 'app-organization-form',
  imports: [ReactiveFormsModule, FormsModule, RouterLink, PageHeader],
  templateUrl: './organization-form.html',
})
export class OrganizationForm implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly service = inject(OrganizationService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly isEdit = signal(false);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);

  protected readonly domains = signal<string[]>([]);
  protected domainInput = '';

  private id?: number;

  protected readonly form = this.fb.group({
    name: ['', Validators.required],
    notes: [''],
  });

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.id = Number(idParam);
      this.isEdit.set(true);
      this.service.get(this.id).subscribe({
        next: (detail) => {
          this.form.patchValue({ name: detail.organization.name, notes: detail.organization.notes ?? '' });
          this.domains.set([...detail.organization.domains]);
        },
      });
    }
  }

  protected addDomain(): void {
    const value = this.domainInput.trim().toLowerCase();
    if (value && !this.domains().includes(value)) {
      this.domains.update((arr) => [...arr, value]);
    }
    this.domainInput = '';
  }

  protected removeDomain(domain: string): void {
    this.domains.update((arr) => arr.filter((d) => d !== domain));
  }

  protected submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    const v = this.form.getRawValue();
    const payload = {
      name: v.name!,
      domains: this.domains(),
      notes: v.notes?.trim() ? v.notes : null,
    };
    const request = this.isEdit() ? this.service.update(this.id!, payload) : this.service.create(payload);
    request.subscribe({
      next: (org) => this.router.navigate(['/', this.auth.currentTenant(), 'organizacoes', org.id]),
      error: (err: HttpErrorResponse) => {
        this.error.set((err.error as ApiError)?.message ?? 'Não foi possível salvar a organização.');
        this.saving.set(false);
      },
    });
  }
}
